import { useCallback, useEffect, useRef } from "react";
import {
  EphemeralWsRequest,
  startEphemeralWsRequest,
} from "@sharpana/shared/utils/EphemeralWsRequest";

function getBaseCoachingWsUrl(): string {
  const host = window.location.host;
  const protocol = window.location.protocol === "http:" ? "ws:" : "wss:";
  return `${protocol}//${host}/ws/coaching/`;
}

interface FrameCallbacks {
  processFrame: (frame: any) => void;
  setActiveRequestId: (id: string | null) => void;
  /**
   * Optional terminal-state hook.  Called when the underlying WS
   * request ends WITHOUT having delivered a ``run_done`` /
   * ``error`` / ``content_done`` frame — the cases where neither
   * ``processFrame`` nor the message-keyed branches in this hook
   * would otherwise clear the loading UI (timeout, ws error before
   * any bytes, server crash mid-stream).  Stores should treat this
   * as "abort the in-flight pending submission".
   */
  onStreamTerminated?: (reason: string) => void;
}

/**
 * Generic ephemeral WS hook for the /ws/coaching/ endpoint.
 *
 * Both coaching and arc-generation features use the same WebSocket
 * endpoint and the same connection lifecycle (open → send action →
 * stream frames → close).  This hook owns the lifecycle; callers
 * provide the store callbacks that differ between features.
 */
export function useEphemeralCoachingWs(callbacks: FrameCallbacks) {
  const { processFrame, setActiveRequestId, onStreamTerminated } = callbacks;
  const requestRef = useRef<EphemeralWsRequest<any, any> | null>(null);
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track which messages we've already processed so we can also
  // safely react to *state-only* notifications (timeout / ws error /
  // close-without-final-frame) without double-processing the last
  // message.  ``EphemeralWsRequest._notify`` is called both on
  // incoming messages AND on state transitions, and the previous
  // implementation only handled the message branch.
  const processedMsgCountRef = useRef(0);
  // True once the FE has seen a terminal frame from the BE
  // (run_done, content_done, or an in-band error).  When that
  // happened, the message-branch above already cleared the spinner
  // — the WS-state branch should NOT additionally fire
  // onStreamTerminated.
  const sawTerminalFrameRef = useRef(false);

  useEffect(() => {
    return () => {
      requestRef.current?.cancel();
      if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
    };
  }, []);

  const sendAction = useCallback(
    (action: string, payload: Record<string, unknown> = {}) => {
      // Supersede any in-flight request.  Clear the ref BEFORE
      // cancelling the old request so its cancel notification sees
      // it is no longer the active request and does NOT fire
      // ``onStreamTerminated`` — a deliberate supersede (e.g. the
      // learner starting the hero quest mid-stream, which cancels
      // the quest_recommendation stream) is not a stream failure
      // and must not flash an error.
      const superseded = requestRef.current;
      requestRef.current = null;
      if (superseded) {
        superseded.cancel();
      }
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
        safetyTimeoutRef.current = null;
      }

      const wsPayload = { action, ...payload };
      const request = startEphemeralWsRequest<any, any>({
        url: getBaseCoachingWsUrl(),
        payload: wsPayload,
        // 90s idle timeout — reasoning models can take 20-30s for
        // first token on a long structured-output prompt.  The old
        // 30s default fired before the first frame ever arrived
        // and produced a stuck "Sending your answer…" spinner with
        // no error visible (the WS closed silently).  Tightening
        // the spinner-clear path below + raising this is the
        // belt-and-suspenders fix.
        timeoutMs: 90000,
      });

      requestRef.current = request;
      processedMsgCountRef.current = 0;
      sawTerminalFrameRef.current = false;
      setActiveRequestId(request.id);

      request.subscribe((req) => {
        // Branch 1: any new messages since last notify.  Iterate so
        // we don't lose intermediate frames that may have been
        // delivered between subscribe-callback invocations.
        const total = req.messages.length;
        while (processedMsgCountRef.current < total) {
          const frame = req.messages[processedMsgCountRef.current++];
          processFrame(frame);

          if (
            frame?.kind === "content_done" ||
            frame?.kind === "run_done" ||
            frame?.kind === "error"
          ) {
            sawTerminalFrameRef.current = true;
          }

          if (frame?.kind === "content_done") {
            safetyTimeoutRef.current = setTimeout(() => {
              if (requestRef.current === request) {
                request.cancel();
                requestRef.current = null;
                setActiveRequestId(null);
              }
            }, 10000);
          }

          if (frame?.kind === "run_done") {
            if (safetyTimeoutRef.current) {
              clearTimeout(safetyTimeoutRef.current);
              safetyTimeoutRef.current = null;
            }
          }
        }

        // Branch 2: terminal WS-state transitions WITHOUT a final
        // frame.  This is the silent-failure fix.  When the WS
        // request lands in error / canceled (timeout, ws error,
        // server crash mid-stream) and no terminal frame ever
        // delivered, push the abort signal upward so the store
        // can clear ``isStreaming`` + the optimistic
        // ``submission.pending`` flag.  Without this the spinner
        // sticks forever.
        if (
          (req.state === "error" || req.state === "canceled") &&
          !sawTerminalFrameRef.current &&
          requestRef.current === request
        ) {
          sawTerminalFrameRef.current = true; // guard re-entry
          if (safetyTimeoutRef.current) {
            clearTimeout(safetyTimeoutRef.current);
            safetyTimeoutRef.current = null;
          }
          requestRef.current = null;
          if (onStreamTerminated) {
            onStreamTerminated(req.errorReason ?? req.state);
          } else {
            // No store-specific abort hook — at minimum clear the
            // active request id so any spinner gated on it goes
            // away.
            setActiveRequestId(null);
          }
        }
      });

      return request;
    },
    [processFrame, setActiveRequestId, onStreamTerminated],
  );

  const cancelRequest = useCallback(() => {
    requestRef.current?.cancel();
    requestRef.current = null;
    setActiveRequestId(null);
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
  }, [setActiveRequestId]);

  return { sendAction, cancelRequest };
}
